import { Component, Input, OnInit, ViewEncapsulation } from '@angular/core';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { TranslateService } from '@ngx-translate/core';
import { SelectGroupMembersDialogComponent } from 'app/library/group/components/dialogs/select-group-members-dialog/select-group-members-dialog.component';
import { SidenavService } from 'app/library/grow/services/sidenav.service';
import { IAddTeamLeadersActionPayload } from 'app/library/team/models/add-team-leaders-action.action.payload';
import { IRemoveTeamLeadersActionPayload } from 'app/library/team/models/remove-team-leaders-action.action.payload';
import { TeamService } from 'app/library/team/services/team.service';
import { SnackBarTime } from 'app/projects/core/src/lib/constants/snack-bar';
import { UnsubscribeOnDestroy } from 'app/projects/core/src/lib/models/unsubscribe-on-destroy';
import { MaxBrainConfirmDialogComponent } from 'app/projects/shared/src/lib/components/dialogs/confirm/confirm.component';
import { User } from 'app/projects/user/src/lib/models/user';
import { Observable } from 'rxjs';
import { AddUserToTeamDialogComponent } from '../dialogs/add-user-to-team-dialog/add-user-to-team-dialog.component';

@Component({
    selector: 'app-team-leaders',
    templateUrl: './team-leaders.component.html',
    styleUrls: ['./team-leaders.component.scss'],
    encapsulation: ViewEncapsulation.None,
})
export class TeamLeadersComponent extends UnsubscribeOnDestroy implements OnInit {
    @Input()
    teamId: string;

    @Input()
    isTeamDetailsPage: string;

    @Input()
    canManageTeamLeaders: boolean;

    @Input()
    isTrackSubTeamProgressActive: boolean;

    @Input()
    allUser$: Observable<User[]>;

    @Input()
    filteredAllUsers$: Observable<User[]>;

    confirmDialogRef: MatDialogRef<MaxBrainConfirmDialogComponent>;
    addUserToTeamDialogRef: MatDialogRef<AddUserToTeamDialogComponent>;

    constructor(
        public sideNav: SidenavService,
        public teamService: TeamService,
        private _matDialog: MatDialog,
        private _translateService: TranslateService,
        private _matSnackBar: MatSnackBar
    ) {
        super();
    }

    async ngOnInit(): Promise<void> {}

    // Method to open the appropriate dialog based on isTeamDetailsPage
    openAddDialog(): void {
        if (this.isTeamDetailsPage) {
            this.openAddUserToTeamDialog();
        } else {
            this.openAddLeaderDialog();
        }
    }

    openAddLeaderDialog(): void {
        const dialogRef = this._matDialog.open(SelectGroupMembersDialogComponent, {
            width: '680px',
            id: 'select-users-dialog',
        });

        dialogRef.componentInstance.entitie$ = this.allUser$;
        dialogRef.componentInstance.entityId = this.teamId;
        dialogRef.componentInstance.title = 'TEAMS.LEAD.BUTTON.ADD_LEAD';
        dialogRef.componentInstance.dataType = 'users';
        dialogRef.componentInstance.role = 'teamLeader';
        dialogRef.componentInstance.showAssignAsTrainingApproverToggle = true;
        dialogRef.componentInstance.assignAsTrainingResponsible = true;

        dialogRef.afterClosed().subscribe(async (result: { list: User[]; assignAsTrainingResponsible?: boolean }) => {
            if (result && result.list) {
                const payload: IAddTeamLeadersActionPayload = {
                    userIds: result.list.map((user) => user.id),
                    teamIds: [this.teamId],
                    assignAsTrainingResponsible: result.assignAsTrainingResponsible,
                };

                await this.teamService.addUsersToTeamAsLeaders(payload);
                await this.teamService.fetchTeamLeaders(this.teamId);
                this.teamService.requestRefreshTeamTrainingResponsibles(this.teamId);
            }
        });
    }

    async openAddUserToTeamDialog(): Promise<void> {
        await this.teamService.fetchTeamLeaderTeams();

        this.addUserToTeamDialogRef = this._matDialog.open(AddUserToTeamDialogComponent, {
            disableClose: false,
            width: '900px',
            autoFocus: false,
        });

        const teams = this.teamService.getTeamLeaderTeams();
        const clonedTeams = JSON.parse(JSON.stringify(teams));
        const teamListTree = this.teamService.convertListToTree(clonedTeams);

        this.addUserToTeamDialogRef.componentInstance.entities$ = this.filteredAllUsers$;
        this.addUserToTeamDialogRef.componentInstance.title = this._translateService.instant('TEAMS.LEAD.BUTTON.ADD_LEAD');
        this.addUserToTeamDialogRef.componentInstance.teamId = this.teamId;
        this.addUserToTeamDialogRef.componentInstance.role = 'teamLeader';
        this.addUserToTeamDialogRef.componentInstance.assignAsTrainingResponsible = true;
        this.addUserToTeamDialogRef.componentInstance.listTree = teamListTree;

        this.addUserToTeamDialogRef.afterClosed().subscribe(async (data: { selectedNodes: Set<User>; assignAsTrainingResponsible?: boolean }) => {
            if (data) {
                const userIds: string[] = Array.from(data.selectedNodes).map((user) => user.id);

                const payload: IAddTeamLeadersActionPayload = {
                    userIds: userIds,
                    teamIds: [this.teamId],
                    assignAsTrainingResponsible: data.assignAsTrainingResponsible,
                };

                const result = await this.teamService.addUsersToTeamAsLeaders(payload);
                await this.teamService.fetchTeamLeaders(this.teamId);
                this.teamService.requestRefreshTeamTrainingResponsibles(this.teamId);

                if (result) {
                    const successMessage = this._translateService.instant('TEAMS.DIALOG.ADD_USER_TO_TEAM.LEADER.MESSAGE.SUCCESS');

                    this._matSnackBar.open(successMessage, 'OK', {
                        verticalPosition: 'top',
                        duration: SnackBarTime.medium,
                    });
                }
            }
            this.addUserToTeamDialogRef = null;
        });
    }

    openRemoveTeamLeaderDialog(teamLeader: User): void {
        this.confirmDialogRef = this._matDialog.open(MaxBrainConfirmDialogComponent, {
            disableClose: false,
        });

        this.confirmDialogRef.componentInstance.confirmTitle = 'GENERAL.BUTTON.CONFIRM';
        this.confirmDialogRef.componentInstance.confirmButtonColor = 'warn';
        this.confirmDialogRef.componentInstance.confirmMessage = this._translateService.instant('TEAMS.DIALOG.REMOVE_TEAM_LEADER.MESSAGE', { fullName: teamLeader.fullName });
        this.confirmDialogRef.componentInstance.actionButtonText = 'GENERAL.BUTTON.REMOVE';

        this.confirmDialogRef.afterClosed().subscribe(async (result) => {
            if (result) {
                const payload: IRemoveTeamLeadersActionPayload = {
                    userIds: [teamLeader.id],
                    teamId: this.teamId,
                };

                await this.teamService.removeTeamLeaders(payload);
                // Refetch team progress
                await this.teamService.fetchTeamWithSubTeamsProgress(this.teamId, this.isTrackSubTeamProgressActive);
            }

            this.confirmDialogRef = null;
        });
    }
}
